/**
 * MaterialSelector.tsx - 材料选择组件
 *
 * 功能说明：
 * - 材料名称自动完成输入
 * - 自动填充预设材料信息
 *
 * 依赖关系：
 * - 依赖 antd AutoComplete
 * - 依赖 services/presetDataService.ts
 *
 * 关键注释索引：
 * - [预设材料] - 见下方 usePresetMaterials hook
 *
 * 创建日期：2026-05-03
 * 作者：AI Assistant
 */

import React, { useMemo } from 'react';
import { AutoComplete } from 'antd';

interface MaterialSelectorProps {
  value?: string;
  onChange?: (value: string) => void;
  onSelect?: (value: string) => void;
  presetMaterialsData?: any;
  disabled?: boolean;
}

/**
 * [材料选择器] 材料名称输入和选择组件
 *
 * @description 支持：
 * - 从预设材料列表选择
 * - 手动输入新材料名称
 * - 自动显示材料的默认单位
 */
const MaterialSelector: React.FC<MaterialSelectorProps> = ({
  value,
  onChange,
  onSelect,
  presetMaterialsData,
  disabled = false,
}) => {
  /**
   * [预设材料选项] 格式化预设材料数据为 AutoComplete 选项
   */
  const materialOptions = useMemo(() => {
    const presetMaterials = presetMaterialsData?.data || [];
    const seen = new Set<string>();

    return presetMaterials
      .filter((m: any) => {
        if (!m.materialName || seen.has(m.materialName)) return false;
        seen.add(m.materialName);
        return true;
      })
      .map((m: any) => ({
        value: m.materialName,
        label: (
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
            <span>{m.materialName}</span>
            {m.defaultUnit && (
              <span style={{ color: '#999', fontSize: 12 }}>{m.defaultUnit}</span>
            )}
          </div>
        ),
      }));
  }, [presetMaterialsData]);

  return (
    <AutoComplete
      options={materialOptions}
      placeholder="输入或选择材料名称"
      allowClear
      filterOption={(input, option) =>
        String(option?.value ?? '')
          .toLowerCase()
          .includes(input.toLowerCase())
      }
      onChange={onChange}
      onSelect={onSelect}
      value={value}
      disabled={disabled}
      style={{ width: '100%' }}
    />
  );
};

export default MaterialSelector;

/**
 * [获取材料默认单位] 从预设材料数据中获取默认单位
 *
 * @param materialName - 材料名称
 * @param presetMaterialsData - 预设材料数据
 * @returns 默认单位字符串
 */
export const getMaterialDefaultUnit = (
  materialName: string,
  presetMaterialsData?: any
): string => {
  const presetMaterials = presetMaterialsData?.data || [];
  const material = presetMaterials.find(
    (m: any) => m.materialName === materialName
  );
  return material?.defaultUnit || '';
};
